import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import SearchPage from './Pages/SearchPage/SearchPage';
import { EstimatorDashboardPropTypes } from 'src/interfaces/iproptypes';
import ReportPage from './Pages/ReportPage/ReportPage';
import { callApi } from 'src/core/callApi';
import { FullScreenAnimationLoader } from 'src/App/Components/Loaders';
import AutohideSnackbar from 'src/App/Components/Alerts/AutohideSnackbar';
import { convertApiCurrency } from './helper';
import { deepCopyObject, getUserPreferredCurrency } from 'src/utils/functions';
import { User } from 'src/interfaces/iuser';
import { useDispatch } from 'react-redux';
import { deductEstimatorCreditAction } from 'src/store/Dashboard/actions';
import { geocodeAddress } from 'src/utils/functions';
import ComingSoonModal from 'src/App/Modals/ComingSoonModal';
import MarketComparePage from '../MarketCompare/MarketComparePage';
import { Button } from '@mui/material';
import { useSetUserPaymentInterest } from 'src/hooks/useCustomHooks';

const getCreateReportBodyFromHistoryState = async estimatorHistoryState => {
  const address_full = estimatorHistoryState.address_full;

  let latitude;
  let longitude;
  const coord = await geocodeAddress(address_full);
  if (coord && Array.isArray(coord) && coord[0] !== 0 && coord[1] !== 0) {
    latitude = coord[0];
    longitude = coord[1];
  }

  const address_short = estimatorHistoryState.address_short
    ? estimatorHistoryState.address_short
    : '';
  const bedrooms = estimatorHistoryState.bedrooms ? parseFloat(estimatorHistoryState.bedrooms) : 2;
  const report_type = estimatorHistoryState.report_type
    ? estimatorHistoryState.report_type
    : 'manage';
  const property_price = estimatorHistoryState.property_price
    ? parseFloat(estimatorHistoryState.property_price)
    : null;
  const monthly_rent = estimatorHistoryState.monthly_rent
    ? parseFloat(estimatorHistoryState.monthly_rent)
    : null;
  let currentReportType = report_type;

  const createRequestObj: { [key: string]: string | number | null } = {
    address_full,
    address_short,
    latitude,
    longitude,
    bedrooms,
    report_type,
    property_price,
    monthly_rent,
  };

  // update local storage with landing report_type
  if (report_type && report_type !== '') {
    localStorage.setItem('landing_report_type', report_type);
  }

  if (
    report_type === 'buy' &&
    (!createRequestObj.property_price || createRequestObj.property_price == 0)
  ) {
    currentReportType = 'manage';
  }

  if (
    report_type === 'rent' &&
    (!createRequestObj.monthly_rent || createRequestObj.monthly_rent == 0)
  ) {
    currentReportType = 'manage';
  }

  if (currentReportType === 'manage') {
    delete createRequestObj.property_price;
    delete createRequestObj.monthly_rent;
    createRequestObj.report_type = 'manage';
  }

  if (currentReportType === 'buy') {
    delete createRequestObj.monthly_rent;
  }

  if (currentReportType === 'rent') {
    delete createRequestObj.property_price;
  }
  return createRequestObj;
};

const handleCreditDeduction = (user: User, dispatch) => {
  const isUserHavingCredits: boolean = user.estimatorCredits > 0;
  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  if (isUserHavingCredits && !isUserSubscribed) {
    dispatch(deductEstimatorCreditAction(1));
  }
};

const EstimatorPage = ({
  setSubscriptionModalOpen,
  user,
  isLoggedIn,
  loginModalOpen,
  setLoginModalOpen,
  matches,
  currency,
  setIsEmailVerifyModalOpen,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  dummyMarket,
  loginCountryCode,
  setEstimatorSimplePricingModalViewMode,
  dummyOverviewMarket,
  setShowTrial,
  handleOnSampleClick,
  resetCurrencyToDefault,
  OnBoardingSearchBox,
  openComparePage,
  setOpenComparePage,
  searchbarOnly,
}: EstimatorDashboardPropTypes) => {
  const estimatorHistoryState = window.history.state;

  const pathname = window.location.pathname;
  const reportId = pathname.split('/')[3];
  const isReportPage = reportId !== '';

  const dispatch = useDispatch();

  const [savedReports, setSavedReports] = useState({ data: null, loading: false });
  const [reportData, setReportData] = useState<any>(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reportErrorMessage, setReportErrorMessage] = useState(null);
  const [reportUpdated, setReportUpdated] = useState(false);
  const [currencyHistory, setCurrencyHistory] = useState<string[]>([]);
  const [reportUpdateCount, setReportUpdateCount] = useState(0);
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);

  const setReportComparePageOpen = () => {
    setOpenComparePage(true);
  };

  const setReportComparePageClose = () => {
    setOpenComparePage(false);
  };

  useEffect(() => {
    resetCurrencyToDefault();
    if (!reportId && reportData) {
      setReportData(_ => null);
    }
  }, [reportId]);

  useEffect(() => {
    if (isLoggedIn && !user.isVerified) {
      setIsEmailVerifyModalOpen(true);
    }
  }, [user, isLoggedIn]);

  const { triggerPaymentInterest } = useSetUserPaymentInterest();

  const handleLoginOrPricingModalOpen = (
    state: 'open' | 'close',
    forceOpen: 'payment' | 'emailVerify' | null = null,
    estimatorSimplePricingModalViewMode: 'expert' | 'scale' = 'expert',
    triggerPaymentInterestAllowed: boolean = true
  ) => {
    setEstimatorSimplePricingModalViewMode(estimatorSimplePricingModalViewMode);

    if (user.email === 'Trial') {
      setLoginModalOpen(state === 'open' ? true : false);
      return;
    }

    if (forceOpen === 'payment') {
      triggerPaymentInterestAllowed && triggerPaymentInterest();
      setSubscriptionModalOpen(true);
      return;
    }

    if (forceOpen === 'emailVerify') {
      setIsEmailVerifyModalOpen(true);
      return;
    }

    if (user.estimatorCredits > 0 || user.subscriptionPlan !== 'starter') {
      return;
    }

    setSubscriptionModalOpen(state === 'open' ? true : false);
    triggerPaymentInterestAllowed && state === 'open' && triggerPaymentInterest();

    return;
  };

  const handleCreateNewReport = () => {
    setReportData(null);
    window.history.pushState({}, '', '/airbnb-calculator');
  };

  const convertReportDataCurrency = (reportData: any, currency: string, prevCurrency: string) => {
    let convertedApiObject: any = null;
    if (prevCurrency === 'USD') {
      convertedApiObject = convertApiCurrency(deepCopyObject(reportData), currency);
    } else {
      const convertedApiObjectToUSD = convertApiCurrency(
        deepCopyObject(reportData),
        prevCurrency,
        true
      );
      convertedApiObject = convertApiCurrency(convertedApiObjectToUSD, currency);
    }
    return convertedApiObject;
  };

  const convertReportBodyIntoUSD = (reportData: any, currency: string) => {
    const convertedApiObject = convertApiCurrency(deepCopyObject(reportData), currency, true, true);
    return convertedApiObject;
  };

  const handleReport = async (
    method,
    requestBody: null | any,
    id?: string,
    storeID = false,
    hideLoading = false
  ) => {
    !hideLoading && setIsReportLoading(true);
    setReportErrorMessage(null);
    setReportUpdated(false);
    method === 'DELETE' && setSavedReports({ data: savedReports.data, loading: true });

    const url =
      'https://3iugob4wql.execute-api.us-east-2.amazonaws.com/prod/airbnbCalculatorReportCRUD';
    let data;

    try {
      switch (method) {
        case 'CREATE':
          requestBody = convertReportBodyIntoUSD(requestBody, currency);
          data = await callApi(url, { body: requestBody }, true, false, '', 'POST', true);
          break;
        case 'READ':
          data = await callApi(url, { queryParams: { id } }, true, false, '', 'GET', true);
          break;
        case 'UPDATE':
          requestBody = convertReportBodyIntoUSD(requestBody, currency);
          data = await callApi(url, { body: requestBody }, true, false, '', 'PUT', true);
          break;
        case 'DELETE':
          data = await callApi(url, { queryParams: { id } }, true, false, '', 'DELETE', true);
          break;
        default:
          console.error(`Unknown method: ${method}`);
          setIsReportLoading(false);
          return;
      }

      const isCurrencyNeedsToBeConverted = currency !== 'USD';

      if (data.error) {
        setReportErrorMessage(data.error);
        method === 'DELETE' && setSavedReports({ data: savedReports.data, loading: false });
      } else if (method === 'DELETE') {
        setSavedReports({ data, loading: false });
      } else {
        !hideLoading && setReportData(null);
        const { url_path } = data.primary_input;

        if (hideLoading) {
          // for asynchronous polling
          setReportData(prevData => {
            const newData = isCurrencyNeedsToBeConverted
              ? convertApiCurrency(data, currency)
              : data;
            return prevData?.primary_input?.comps_status !== newData?.primary_input?.comps_status
              ? newData
              : prevData;
          });
        } else {
          setReportData(_ =>
            isCurrencyNeedsToBeConverted ? convertApiCurrency(data, currency) : data
          );
        }

        storeID && localStorage.setItem('estimator-unmapped-report', data.primary_input.id);

        url_path && window.history.replaceState({}, '', `${window.location.origin}/${url_path}`);
        method === 'UPDATE' && setReportUpdated(true);
        method === 'CREATE' && handleCreditDeduction(user, dispatch);
      }
    } catch (error) {
      console.error('Error handling report:', error);
    } finally {
      setIsReportLoading(false);
    }
  };

  useEffect(() => {
    if (user.isVerified) {
      const unmappedReportID = localStorage.getItem('estimator-unmapped-report');
      if (!unmappedReportID) {
        return;
      }

      const authenticateReportObj = {
        update_type: 'authenticate_report',
        id: unmappedReportID,
      };
      handleReport('UPDATE', authenticateReportObj);

      localStorage.removeItem('estimator-unmapped-report');
    }
  }, [user.isVerified]);

  useEffect(() => {
    if (isReportPage && reportId && !searchbarOnly) {
      handleReport('READ', null, reportId);
    }
  }, []);

  const getUpdatedCurrencyHistory = () => {
    const newCurrencyHistory = [...currencyHistory];
    newCurrencyHistory.push(currency);
    newCurrencyHistory.length > 2 && newCurrencyHistory.shift();
    return newCurrencyHistory;
  };

  //Maintains the currency history so I will know which currency it was previously
  useEffect(() => {
    const updatedCurrencyHistory = getUpdatedCurrencyHistory();
    setCurrencyHistory(_ => updatedCurrencyHistory);
  }, [currency]);

  useEffect(() => {
    if (!reportData) return;

    const prevCurrency = currencyHistory[0];
    const convertedApiObject: any = convertReportDataCurrency(reportData, currency, prevCurrency);

    setReportData(_ => convertedApiObject);
  }, [currencyHistory]);

  useEffect(() => {
    setReportUpdateCount(_ => reportUpdateCount + 1);
  }, [reportData]);

  useEffect(() => {
    const initializeReportCreation = async () => {
      if (window.google) {
        if (!estimatorHistoryState?.address_full) return;

        const createReportBody = await getCreateReportBodyFromHistoryState(estimatorHistoryState);

        if (user.email === 'Trial') {
          if (!createReportBody) return;
          handleReport('CREATE', createReportBody, undefined, true);
          setLoginModalOpen(true);
        }
      }
    };
    initializeReportCreation();
  }, [window.google]);

  if (openComparePage) {
    return (
      <MarketComparePage
        user={user}
        currency={currency}
        isLoggedIn={isLoggedIn}
        dummyMarket={dummyMarket}
        handleOnSampleClick={handleOnSampleClick}
        OnBoardingSearchBox={OnBoardingSearchBox}
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        isMobileScreen={matches}
        source="report"
        handleReport={handleReport}
        setReportComparePageClose={setReportComparePageClose}
      />
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Airbnb Profit Calculator: How much can I make on Airbnb?</title>
      </Helmet>

      {!reportData && !isReportLoading && (
        <SearchPage
          user={user}
          matches={matches}
          currency={currency}
          dummyMarket={dummyMarket}
          googleAutocompleteSessionToken={googleAutocompleteSessionToken}
          getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
          clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
          googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
          handleReport={handleReport}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          setComingSoonModalOpen={setComingSoonModalOpen}
          savedReports={savedReports}
          setSavedReports={setSavedReports}
          resetCurrencyToDefault={resetCurrencyToDefault}
          setReportComparePageOpen={setReportComparePageOpen}
          searchbarOnly={searchbarOnly}
        />
      )}

      {reportData && (
        <ReportPage
          key={'reportPage-update-' + reportUpdateCount}
          reportData={reportData}
          currency={currency}
          handleCreateNewReport={handleCreateNewReport}
          handleReport={handleReport}
          matches={matches}
          user={user}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          setComingSoonModalOpen={setComingSoonModalOpen}
          dummyOverviewMarket={dummyOverviewMarket}
          loginCountryCode={loginCountryCode}
          handleOnSampleClick={handleOnSampleClick}
          setReportComparePageOpen={setReportComparePageOpen}
        />
      )}

      {!searchbarOnly && isReportLoading && !loginModalOpen && <FullScreenAnimationLoader />}

      {!searchbarOnly && reportErrorMessage && (
        <AutohideSnackbar message={reportErrorMessage} type="error" />
      )}
      {!searchbarOnly && reportUpdated && (
        <AutohideSnackbar message={'Report successfully updated!'} type="success" />
      )}

      {comingSoonModalOpen && (
        <ComingSoonModal open={comingSoonModalOpen} setOpen={setComingSoonModalOpen} />
      )}
    </>
  );
};

export default EstimatorPage;
